import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Portal from '../../../../components/Portal';
import { HiCalendar, HiChartPie, HiChevronDown, HiChevronLeft, HiChevronRight, HiClipboardList, HiCog, HiInformationCircle, HiTrendingUp, HiUserGroup } from 'react-icons/hi';
import moment from 'moment';
import { dashboardStatusHexColors, getDashboardStatusLabel, getItemTypeColor } from '../../../../constants/integrationTypes';
import DonutChart from './DonutChart';
import StatusBar from './StatusBar';
import PersonBar from './PersonBar';
import RiskCard from './RiskCard';
import SectionCustomizer, { SectionConfig, loadSectionPreferences, saveSectionPreferences } from './SectionCustomizer';
import CustomTooltip from '../../../../components/common/CustomTooltip';

interface TaskAnalyticsSidebarProps {
  tasks: any;
  unallocatedTasks: any[];
  groups: any[];
  currentSprint: any;
  sprintMode: boolean;
  currentDate: moment.Moment | null;
  teamFilterState: any;
  searchType: string;
  isOpen: boolean;
  onToggle: () => void;
  stats?: any; // Stats from API response
  isLoading?: boolean; // Loading state for capacity planning API
  selectedTeams?: string[]; // Selected team IDs in sprint filter mode (multiselect)
  onFilterRequest?: (type: 'status' | 'person' | 'project' | 'type', value: string) => void;
  onTotalTasksClick?: () => void; // Handler for Total Tasks card click
  statsPerTeam?: any; // Per-team stats from API response (stats_per_team)
  teamSprints?: any[]; // Sprints displayed based on sprintRangeSelection
  sprintRangeSelection?: 'current' | 'past-current' | 'current-future' | 'all'; // Sprint range selection
  onWidthChange?: (width: number) => void; // Callback when sidebar width changes via resize
}

// Constants for resize constraints
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 384;
const STORAGE_KEY = 'taskAnalyticsSidebarWidth';

const TaskAnalyticsSidebar: React.FC<TaskAnalyticsSidebarProps> = ({
  tasks,
  unallocatedTasks,
  groups,
  currentSprint,
  sprintMode,
  currentDate,
  teamFilterState,
  searchType,
  isOpen,
  onToggle,
  stats,
  isLoading = false,
  selectedTeams = [],
  onFilterRequest,
  onTotalTasksClick,
  statsPerTeam,
  teamSprints = [],
  sprintRangeSelection = 'current',
  onWidthChange
}) => {
  // Get organizationTeams from Redux to look up team info when in sprint mode
  const { teams: organizationTeams } = useSelector((state: any) => state.organization);

  // State for team tooltip
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [showTeamTooltip, setShowTeamTooltip] = useState(false);

  // State for sprint tooltip
  const [sprintTooltipPos, setSprintTooltipPos] = useState({ top: 0, left: 0 });
  const [showSprintTooltip, setShowSprintTooltip] = useState(false);

  // State for resizable width
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    // Load saved width from localStorage
    const savedWidth = localStorage.getItem(STORAGE_KEY);
    if (savedWidth) {
      const parsed = parseInt(savedWidth, 10);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
    return DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  // Scroll indicator state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Section customization state
  const [sectionPreferences, setSectionPreferences] = useState<SectionConfig[]>(() => loadSectionPreferences());
  const [showCustomizer, setShowCustomizer] = useState(false);

  // Create a visibility map for quick lookups - recreated when preferences change
  const sectionVisibilityMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    sectionPreferences.forEach(section => {
      map[section.id] = section.visible;
    });
    return map;
  }, [sectionPreferences]);

  // Helper to check if a section is visible
  const isSectionVisible = (sectionId: string): boolean => {
    return sectionVisibilityMap[sectionId] ?? true;
  };

  // Get ordered sections
  const orderedSections = useMemo(() => {
    return [...sectionPreferences].sort((a, b) => a.order - b.order);
  }, [sectionPreferences]);


  // Section order indices - used to determine rendering order
  const sectionOrderMap = useMemo(() => {
    const map: Record<string, number> = {};
    orderedSections.forEach((section, index) => {
      map[section.id] = index;
    });
    return map;
  }, [orderedSections]);

  // Check if there's more content to scroll
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Show indicator if there's more than 100px left to scroll
      const hasMoreContent = scrollHeight - scrollTop - clientHeight > 100;
      setShowScrollIndicator(hasMoreContent);
    }
  }, []);

  // Scroll to bottom when indicator is clicked
  const handleScrollDown = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ top: 300, behavior: 'smooth' });
    }
  }, []);

  // Check scroll position on mount and when content changes
  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Also check after a small delay to account for content loading
      const timer = setTimeout(checkScrollPosition, 500);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        clearTimeout(timer);
      };
    }
  }, [checkScrollPosition, isOpen, isLoading]);

  // ===== RESIZE HANDLING =====
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  }, [sidebarWidth]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    // Calculate delta (moving left = increase width since sidebar is on right)
    const delta = resizeStartX.current - e.clientX;
    const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeStartWidth.current + delta));
    setSidebarWidth(newWidth);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, String(sidebarWidth));
      // Notify parent of width change
      onWidthChange?.(sidebarWidth);
    }
  }, [isResizing, sidebarWidth, onWidthChange]);

  // Attach global mouse events for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Notify parent of initial width on mount
  useEffect(() => {
    if (isOpen) {
      onWidthChange?.(sidebarWidth);
    }
  }, [isOpen]);

  // ===== DATA PROCESSING =====
  const analytics = useMemo(() => {
    const analyticsStats = {
      total: 0,
      byStatus: {} as Record<string, { count: number; points: number; originalEstimate: number; color: string; label: string }>,
      byPerson: {} as Record<string, {
        count: number;
        points: number;
        completedPoints: number;
        originalEstimate: number;
        completedOriginalEstimate: number;
        computedPoints: number;
        completedComputedPoints: number;
        computedOriginalEstimate: number;
        completedComputedOriginalEstimate: number;
        name: string;
        byStatus: Record<string, number>;
        byType: Record<string, number>
      }>,
      byProject: {} as Record<string, { count: number; points: number; originalEstimate: number; name: string }>
    };

    const processTask = (task: any, personId?: string, personName?: string) => {
      analyticsStats.total++;
      const points = parseFloat(task.story_points || '0');
      const originalEstimate = parseFloat(task.original_estimate || '0');
      const completedStoryPoints = parseFloat(task.completed_story_points || '0');
      const completedOriginalEstimate = parseFloat(task.completed_original_estimate || '0');
      const status = task.status?.toLowerCase() || 'unknown';

      // By Status
      if (!analyticsStats.byStatus[status]) {
        analyticsStats.byStatus[status] = {
          count: 0,
          points: 0,
          originalEstimate: 0,
          color: dashboardStatusHexColors[status] || '#9ca3af',
          label: getDashboardStatusLabel(status)
        };
      }
      analyticsStats.byStatus[status].count += 1;
      analyticsStats.byStatus[status].points += points;
      analyticsStats.byStatus[status].originalEstimate += originalEstimate;



      // By Project
      const projectName = task.project_name || 'Unknown';
      if (!analyticsStats.byProject[projectName]) {
        analyticsStats.byProject[projectName] = { count: 0, points: 0, originalEstimate: 0, name: projectName };
      }
      analyticsStats.byProject[projectName].count += 1;
      analyticsStats.byProject[projectName].points += points;
      analyticsStats.byProject[projectName].originalEstimate += originalEstimate;

      // Track per person if personId is provided
      if (personId) {
        if (!analyticsStats.byPerson[personId]) {
          analyticsStats.byPerson[personId] = {
            count: 0,
            points: 0,
            completedPoints: 0,
            originalEstimate: 0,
            completedOriginalEstimate: 0,
            computedPoints: 0,
            completedComputedPoints: 0,
            computedOriginalEstimate: 0,
            completedComputedOriginalEstimate: 0,
            name: personName || `Resource ${personId}`,
            byStatus: {},
            byType: {}
          };
        }
        analyticsStats.byPerson[personId].count += 1;
        analyticsStats.byPerson[personId].points += points;
        analyticsStats.byPerson[personId].originalEstimate += originalEstimate;

        // Use task's completed values directly
        analyticsStats.byPerson[personId].completedPoints += completedStoryPoints;
        analyticsStats.byPerson[personId].completedOriginalEstimate += completedOriginalEstimate;

        // Track computed values
        const integrationTags = task.integration_data?.integration_tags || {};

        const isStoryPointsComputed = !!task.story_points_computed || !!integrationTags.story_points_computed;
        const isCompletedSPComputed = !!task.completed_story_points_computed || !!integrationTags.completed_story_points_computed;
        const isOriginalEstimateComputed = !!task.original_estimate_computed || !!integrationTags.original_estimate_computed;
        const isCompletedOEComputed = !!task.completed_original_estimate_computed || !!integrationTags.completed_original_estimate_computed;

        if (isStoryPointsComputed) {
          analyticsStats.byPerson[personId].computedPoints += points;
        }

        if (isCompletedSPComputed) {
          analyticsStats.byPerson[personId].completedComputedPoints += completedStoryPoints;
        }

        if (isOriginalEstimateComputed) {
          analyticsStats.byPerson[personId].computedOriginalEstimate += originalEstimate;
        }

        if (isCompletedOEComputed) {
          analyticsStats.byPerson[personId].completedComputedOriginalEstimate += completedOriginalEstimate;
        }

        // Track by status
        if (!analyticsStats.byPerson[personId].byStatus[status]) {
          analyticsStats.byPerson[personId].byStatus[status] = 0;
        }
        analyticsStats.byPerson[personId].byStatus[status] += 1;

        // Categorize by type
        const taskType = task.integration_item_type?.toLowerCase() || 'task';

        if (!analyticsStats.byPerson[personId].byType[taskType]) {
          analyticsStats.byPerson[personId].byType[taskType] = 0;
        }
        analyticsStats.byPerson[personId].byType[taskType] += 1;
      }
    };

    // Process Allocated Tasks (grouped by person)
    if (tasks) {
      Object.entries(tasks).forEach(([personId, dateMap]: [string, any]) => {
        const person = groups.find(g => g.id === personId || g.id === parseInt(personId));
        // Try multiple properties to get the actual person name
        const personName = person?.title || person?.name || person?.label || person?.full_name || person?.display_name || `Resource ${personId}`;

        if (dateMap && typeof dateMap === 'object') {
          // Process tasks for this person, passing personId to track metrics
          Object.values(dateMap).forEach((taskList: any) => {
            if (Array.isArray(taskList)) {
              taskList.forEach(task => processTask(task, personId, personName));
            }
          });
        }
      });
    }

    // Process Unallocated Tasks
    unallocatedTasks.forEach(task => processTask(task));

    return analyticsStats;
  }, [tasks, unallocatedTasks, groups]);

  const chartData = Object.values(analytics.byStatus).map(item => ({
    value: item.count,
    color: item.color,
    label: item.label,
    originalStatus: Object.keys(analytics.byStatus).find(key => analytics.byStatus[key] === item) || ''
  }));

  const maxPersonTasks = Math.max(...Object.values(analytics.byPerson).map(p => p.count), 1);

  // Calculate risk counts
  const riskCounts = useMemo(() => {
    const delayedCount = stats?.overall_status_breakdown?.delayed || analytics.byStatus['delayed']?.count || 0;
    const unallocatedCount = stats?.overall_status_breakdown?.unallocated ||
      stats?.overall_status_breakdown?.not_responsible ||
      analytics.byStatus['unallocated']?.count ||
      analytics.byStatus['not_responsible']?.count ||
      0;
    const blockedCount = stats?.overall_status_breakdown?.blocked || analytics.byStatus['blocked']?.count || 0;
    const riskCount = delayedCount + unallocatedCount + blockedCount;
    return { delayedCount, unallocatedCount, blockedCount, riskCount };
  }, [stats, analytics]);

  // Sprint & Date Info
  const sprintName = currentSprint?.name || 'No Sprint';

  // Calculate if we should show sprint count indicator
  // Conditions: sprint filter active, single team selected, and sprintRangeSelection is not 'current'
  const shouldShowSprintCount = searchType === 'sprint' &&
    selectedTeams.length === 1 &&
    sprintRangeSelection !== 'current' &&
    teamSprints &&
    teamSprints.length > 1;

  // Helper function to get badge color classes based on sprint status
  const getSprintStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'Current':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'Past':
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300 border-gray-200 dark:border-gray-600';
      case 'Future':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 border-amber-200 dark:border-amber-700';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  // Get sprint info with names and status for tooltip and Sprint Comparison
  // This is optimized to compute once and reuse in both places
  const sprintInfoForDisplay = useMemo(() => {
    if (!shouldShowSprintCount || !teamSprints || teamSprints.length === 0) return [];

    return teamSprints.map((sprint: any) => {
      const name = sprint?.name || '';
      // Determine status label based on sprint status
      let statusLabel = 'Future';
      if (sprint?.status === 'current') {
        statusLabel = 'Current';
      } else if (sprint?.status === 'past' || sprint?.status === 'completed') {
        statusLabel = 'Past';
      }
      return {
        name,
        status: statusLabel,
        fullLabel: `${name} (${statusLabel})`,
        badgeClasses: getSprintStatusBadgeClasses(statusLabel)
      };
    }).filter((s: any) => s.name);
  }, [shouldShowSprintCount, teamSprints]);

  // Create a map for quick lookup of sprint status by name
  const sprintStatusMap = useMemo(() => {
    const map: Record<string, string> = {};
    sprintInfoForDisplay.forEach((s: any) => {
      map[s.name] = s.status;
    });
    return map;
  }, [sprintInfoForDisplay]);

  // Get team name and project name
  // When in sprint filter mode with teams selected, look up team info from organizationTeams
  // Otherwise, use teamFilterState (for team filter mode)
  let teamName = 'All Teams';
  let projectName = ''; // Default to empty string instead of 'No Project'
  let selectedTeamNames: string[] = []; // For tooltip when multiple teams

  if (searchType === 'sprint' && selectedTeams && selectedTeams.length > 0 && organizationTeams) {
    // In sprint filter mode with teams selected
    // Get all selected team names with project names for tooltip
    selectedTeamNames = selectedTeams.map((teamId: string) => {
      const team = organizationTeams.find((t: any) => String(t.id) === String(teamId));
      if (!team?.name) return '';
      const teamProjectName = team.projects?.[0]?.project_name;
      return teamProjectName ? `${team.name} (${teamProjectName})` : team.name;
    }).filter(Boolean);

    if (selectedTeams.length === 1) {
      // Single team selected - show team name
      const team = organizationTeams.find((t: any) => String(t.id) === String(selectedTeams[0]));
      if (team) {
        teamName = team.name || 'All Teams';
        projectName = team.projects?.[0]?.project_name || '';
      }
    } else {
      // Multiple teams selected - show count only (no project name)
      teamName = `${selectedTeams.length} teams`;
      projectName = ''; // Don't show project name for multiple teams
    }
  } else if (teamFilterState?.team_name) {
    // In team filter mode, use teamFilterState
    teamName = teamFilterState.team_name;
    projectName = teamFilterState.team_project_name || '';
  }

  const dateRange = sprintMode && currentSprint && currentSprint.start_date && currentSprint.end_date
    ? `${moment(currentSprint.start_date).format('MMM DD')} - ${moment(currentSprint.end_date).format('MMM DD, YYYY')}`
    : currentDate
      ? `${moment(currentDate).format('MMM DD')} - ${moment(currentDate).add(4, 'weeks').format('MMM DD, YYYY')}`
      : 'Date Range';

  // ===== RENDER =====
  return (
    <Portal>
      <div
        className={`
        transition-all duration-300 ease-in-out bottom-0
        w-16
        flex flex-col fixed right-0 top-[8.8rem] z-[9997]
      `}
      >
        {/* ===== COLLAPSED STATE ===== */}
        {!isOpen && (
          <div
            className="h-full bg-gradient-to-br from-white to-gray-50 dark:from-tertiary-dark dark:to-gray-900 text-light-text-primary dark:text-white border-l border-light-border-default dark:border-gray-700 shadow-2xl flex flex-col"
          >
            <div
              className="flex flex-col items-center py-4 h-full cursor-pointer group"
              onClick={onToggle}
            >
              <button
                className="p-2 rounded-full hover:bg-white/50 dark:hover:bg-slate-700 text-light-text-muted hover:text-primary-light dark:text-gray-400 dark:hover:text-blue-400 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                aria-label="Expand analytics"
              >
                <HiChevronLeft size={22} />
              </button>

              {/* Compact Stats Cards */}
              <div className="flex flex-col items-center gap-3 mt-6 w-full px-2">
                {(isLoading || !stats) ? (
                  <>
                    {/* Loading Skeletons */}
                    <div className="flex flex-col items-center p-2 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-700/30 w-full animate-pulse">
                      <div className="h-6 w-10 bg-blue-300 dark:bg-blue-700 rounded mb-1"></div>
                      <div className="h-2 w-14 bg-blue-200 dark:bg-blue-800 rounded"></div>
                    </div>
                    <div className="flex flex-col items-center p-2 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg border border-purple-200 dark:border-purple-700/30 w-full animate-pulse">
                      <div className="h-6 w-10 bg-purple-300 dark:bg-purple-700 rounded mb-1"></div>
                      <div className="h-2 w-6 bg-purple-200 dark:bg-purple-800 rounded"></div>
                    </div>
                    <div className="flex flex-col items-center p-2 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 rounded-lg border border-teal-200 dark:border-teal-700/30 w-full animate-pulse">
                      <div className="h-6 w-10 bg-teal-300 dark:bg-teal-700 rounded mb-1"></div>
                      <div className="h-2 w-6 bg-teal-200 dark:bg-teal-800 rounded"></div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Total Tasks */}
                    <div
                      className="flex flex-col items-center p-2 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-700/30 w-full hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTotalTasksClick?.();
                      }}
                    >
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{analytics.total}</div>
                      <div className="text-[8px] text-blue-700 dark:text-blue-300 uppercase tracking-wide group-hover:hidden">Work Items</div>
                      <div className="text-[8px] text-blue-600 dark:text-blue-400 font-medium hidden group-hover:block">View all &gt;</div>
                    </div>

                    {/* Story Points */}
                    <div className="flex flex-col items-center p-2 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg border border-purple-200 dark:border-purple-700/30 w-full hover:shadow-md transition-shadow">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {(stats?.total_story_points || 0).toFixed(0)}
                      </div>
                      <div className="text-[8px] text-purple-700 dark:text-purple-300 uppercase tracking-wide">SP</div>
                    </div>

                    {/* Original Estimate */}
                    {stats?.total_original_estimate > 0 && (
                      <div className="flex flex-col items-center p-2 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 rounded-lg border border-teal-200 dark:border-teal-700/30 w-full hover:shadow-md transition-shadow">
                        <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                          {(stats.total_original_estimate).toFixed(0)}
                        </div>
                        <div className="text-[8px] text-teal-700 dark:text-teal-300 uppercase tracking-wide">OE</div>
                      </div>
                    )}

                    {/* Unallocated Story Points */}
                    {stats?.total_unallocated_story_points > 0 && (
                      <div className="flex flex-col items-center p-2 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg border border-amber-200 dark:border-amber-700/30 w-full hover:shadow-md transition-shadow">
                        <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {stats.total_unallocated_story_points}
                        </div>
                        <div className="text-[8px] text-amber-700 dark:text-amber-300 uppercase tracking-wide text-center leading-tight">Unall. SP</div>
                      </div>
                    )}

                    {/* Risk Tasks (Delayed + Blocked + Unallocated) with Warning */}
                    <RiskCard
                      riskCount={riskCounts.riskCount}
                      delayedCount={riskCounts.delayedCount}
                      unallocatedCount={riskCounts.unallocatedCount}
                      blockedCount={riskCounts.blockedCount}
                    />
                  </>
                )}
              </div>

              {/* Vertical Label */}
              <div className="flex-1 flex items-center justify-center mt-auto pb-4">
                <div className="text-[10px] text-light-text-secondary dark:text-gray-400 font-semibold uppercase tracking-wider rotate-180 opacity-70" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                  Analytics
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== EXPANDED STATE (OVERLAY) ===== */}
        <div
          className={`absolute right-0 top-0 h-full bg-gradient-to-br from-white to-gray-50 dark:from-tertiary-dark dark:to-gray-900 text-light-text-primary dark:text-white border-l border-light-border-default dark:border-gray-700 shadow-2xl flex flex-col overflow-hidden z-40 transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
          style={{ width: sidebarWidth }}
        >
          {/* Resize Handle */}
          <div
            className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-50 group"
            onMouseDown={handleResizeStart}
          >
            {/* Visual grip indicator */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 bg-light-surfacePrimary dark:bg-secondary-dark flex flex-row border border-gray-200 dark:border-gray-700 rounded-r-full p-1 gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-0.5 h-4 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
              <div className="w-0.5 h-4 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
            </div>
            {/* Hover highlight */}
            <div className="absolute inset-0 bg-primary-light/10 dark:bg-primary-dark/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {/* Header with Context */}
          <div className="px-5 py-4 border-b border-light-border-default dark:border-gray-700 bg-white dark:bg-slate-800/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow-md">
                  <HiChartPie className="text-white w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                  Sprint Insights
                </h3>
              </div>
              <div className="flex items-center gap-1">
                {/* Customize button */}
                <button
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-all"
                  onClick={() => setShowCustomizer(true)}
                  title="Customize sections"
                >
                  <HiCog size={18} />
                </button>
                {/* Close button */}
                <button
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-all"
                  onClick={onToggle}
                >
                  <HiChevronRight size={20} />
                </button>
              </div>
            </div>

            {/* Context Info - Improved Layout */}
            <div className="space-y-3">
              {/* Sprint and Team in horizontal grid */}
              <div className="grid grid-cols-2 gap-3">
                {sprintMode && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <HiTrendingUp className="w-4 h-4 text-blue-500" />
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Sprint</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="text-base font-bold text-gray-900 dark:text-white truncate">
                        {shouldShowSprintCount ? `${teamSprints.length} sprints` : sprintName}
                      </div>
                      {/* Info button with tooltip for multiple sprints */}
                      {shouldShowSprintCount && (
                        <div
                          className="relative group"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSprintTooltipPos({
                              top: rect.top + rect.height / 2,
                              left: rect.left
                            });
                            setShowSprintTooltip(true);
                          }}
                          onMouseLeave={() => setShowSprintTooltip(false)}
                        >
                          <HiInformationCircle className="w-4 h-4 text-blue-500 hover:text-blue-600 cursor-help flex-shrink-0" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className={sprintMode ? '' : 'col-span-2'}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <HiUserGroup className="w-4 h-4 text-green-500" />
                    <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Team</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={selectedTeamNames.length > 1 ? selectedTeamNames.join(', ') : `${teamName}${projectName ? ` (${projectName})` : ''}`}>
                      {teamName}{projectName ? ` (${projectName})` : ''}
                    </div>
                    {/* Info button with tooltip for multiple teams */}
                    {selectedTeamNames.length > 1 && (
                      <div
                        className="relative group"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPos({
                            top: rect.top + rect.height / 2,
                            left: rect.left
                          });
                          setShowTeamTooltip(true);
                        }}
                        onMouseLeave={() => setShowTeamTooltip(false)}
                      >
                        <HiInformationCircle className="w-4 h-4 text-blue-500 hover:text-blue-600 cursor-help flex-shrink-0" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-4">
                  {/* Date Range */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <HiCalendar className="w-4 h-4 text-purple-500" />
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date Range</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {dateRange}
                    </div>
                  </div>

                  {/* Work Items - Centered & Clickable */}
                  {(isLoading || !stats) ? (
                    <div className="animate-pulse">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-4 h-4 bg-blue-200 dark:bg-blue-800 rounded"></div>
                        <div className="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-8 bg-blue-200 dark:bg-blue-700 rounded"></div>
                        <div className="h-2.5 w-12 bg-blue-100 dark:bg-blue-800 rounded"></div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onTotalTasksClick?.();
                      }}
                      className="cursor-pointer group rounded-lg p-2 -m-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150"
                      title="Click to view all work items"
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <HiClipboardList className="w-4 h-4 text-blue-500" />
                        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Work Items</span>
                      </div>
                      <div className="flex items-center gap-2 pl-1">
                        <span className="text-sm font-bold text-gray-900 dark:text-white transition-colors">{analytics.total}</span>
                        <span className="text-[10px] text-blue-500 dark:text-blue-400 font-medium group-hover:underline">
                          View all &gt;
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto custom-scrollbar p-4 pb-40 space-y-5">
            {(isLoading || !stats) ? (
              <>
                {/* Loading Skeletons for Expanded State */}

                {/* Effort Metrics Skeleton - Minimalist Design */}
                <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700 animate-pulse flex flex-col gap-5">

                  {/* Story Points Section Skeleton */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <div className="h-2 w-16 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      <div className="w-2.5 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {/* Total | Completed row */}
                      <div className="flex items-baseline gap-2">
                        <div className="h-5 w-10 bg-purple-200 dark:bg-purple-800 rounded-md"></div>
                        <div className="h-3 w-px bg-slate-200 dark:bg-slate-600"></div>
                        <div className="h-3.5 w-7 bg-green-200 dark:bg-green-800 rounded-md"></div>
                        <div className="h-2 w-14 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      </div>
                      {/* Unallocated row */}
                      <div className="flex items-baseline gap-1 ml-0.5">
                        <div className="h-2.5 w-2 bg-orange-200 dark:bg-orange-800 rounded-full"></div>
                        <div className="h-3 w-5 bg-orange-200 dark:bg-orange-800 rounded-md"></div>
                        <div className="h-2 w-14 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-100 dark:bg-slate-700/50"></div>

                  {/* Original Estimate Section Skeleton */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <div className="h-2 w-20 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      <div className="w-2.5 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {/* Total | Completed row */}
                      <div className="flex items-baseline gap-2">
                        <div className="h-5 w-12 bg-teal-200 dark:bg-teal-800 rounded-md"></div>
                        <div className="h-3 w-px bg-slate-200 dark:bg-slate-600"></div>
                        <div className="h-3.5 w-8 bg-green-200 dark:bg-green-800 rounded-md"></div>
                        <div className="h-2 w-14 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      </div>
                      {/* Unallocated row */}
                      <div className="flex items-baseline gap-1 ml-0.5">
                        <div className="h-2.5 w-2 bg-orange-200 dark:bg-orange-800 rounded-full"></div>
                        <div className="h-3 w-6 bg-orange-200 dark:bg-orange-800 rounded-md"></div>
                        <div className="h-2 w-14 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Overview Skeleton */}
                <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700 animate-pulse">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 bg-gradient-to-b from-blue-300 to-purple-300 dark:from-blue-700 dark:to-purple-700 rounded-full"></div>
                    <div className="h-3 w-28 bg-gray-200 dark:bg-slate-700 rounded"></div>
                  </div>
                  <div className="flex justify-center mb-3">
                    <div className="w-32 h-32 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
                  </div>
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-slate-600"></div>
                          <div className="h-2.5 w-16 bg-gray-200 dark:bg-slate-700 rounded"></div>
                        </div>
                        <div className="h-2.5 w-8 bg-gray-200 dark:bg-slate-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Workload by Person Skeleton */}
                <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700 animate-pulse">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 bg-gradient-to-b from-green-300 to-teal-300 dark:from-green-700 dark:to-teal-700 rounded-full"></div>
                    <div className="h-3 w-32 bg-gray-200 dark:bg-slate-700 rounded"></div>
                  </div>
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="flex justify-between">
                          <div className="h-2.5 w-24 bg-gray-200 dark:bg-slate-700 rounded"></div>
                          <div className="h-2.5 w-20 bg-gray-200 dark:bg-slate-700 rounded"></div>
                        </div>
                        <div className="h-6 w-full bg-gray-200 dark:bg-slate-700 rounded-full"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Effort Metrics - Minimalist Design */}
                {isSectionVisible('effort-metrics') && (
                  <div style={{ order: sectionOrderMap['effort-metrics'] ?? 0 }} className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700 flex flex-col gap-5">

                    {/* Story Points Section */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Story Points</span>
                        <CustomTooltip
                          title="Maps to Story Points field in Azure DevOps for User Story, Bug, and Spike work items. 'Completed' shows the Actual Story Points value for that work item."
                          placement="top"
                          arrow
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-slate-400 dark:text-slate-500 cursor-help hover:text-slate-600 dark:hover:text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 16v-4"></path>
                            <path d="M12 8h.01"></path>
                          </svg>
                        </CustomTooltip>
                      </div>

                      <div className="flex flex-col gap-0.5">
                        {/* Total • Completed */}
                        <div className="flex items-baseline flex-wrap gap-x-2 text-slate-300 dark:text-slate-600">
                          {/* Total */}
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold text-purple-600 dark:text-purple-400">
                              {(stats?.total_story_points || 0).toFixed(1)}
                            </span>
                            <CustomTooltip
                              title={(stats?.story_points_computed_by_cadence || 0) > 0
                                ? `${(stats?.story_points_computed_by_cadence || 0).toFixed(1)} Story Points computed by Cadence Pro`
                                : 'All Story Points from Azure DevOps'}
                              placement="top"
                              arrow
                            >
                              <span className={`text-[10px] cursor-help ${(stats?.story_points_computed_by_cadence || 0) > 0 ? 'text-yellow-500' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                            </CustomTooltip>
                          </div>

                          <span className="text-slate-300 dark:text-slate-600">|</span>

                          {/* Completed */}
                          <div className="flex items-baseline gap-0.5">
                            <span className={`text-sm font-bold ${(stats?.total_completed_story_points || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-600'}`}>
                              {(stats?.total_completed_story_points || 0).toFixed(1)}
                            </span>
                            <CustomTooltip
                              title={(stats?.completed_story_points_computed_by_cadence || 0) > 0
                                ? `${(stats?.completed_story_points_computed_by_cadence || 0).toFixed(1)} Actual Story Points computed by Cadence Pro`
                                : 'All Actual Story Points from Azure DevOps'}
                              placement="top"
                              arrow
                            >
                              <span className={`text-[10px] cursor-help ${(stats?.completed_story_points_computed_by_cadence || 0) > 0 ? 'text-yellow-500' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                            </CustomTooltip>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Completed</span>
                          </div>
                        </div>

                        {/* Unallocated (New Line) */}
                        {(stats?.total_unallocated_story_points ?? 0) > 0 && (
                          <div className="flex items-baseline gap-0.5 ml-0.5">
                            <span className="text-orange-500 dark:text-orange-400 text-xs font-medium mr-0.5">+</span>
                            <span className="text-sm font-bold text-orange-500 dark:text-orange-400">
                              {stats?.total_unallocated_story_points}
                            </span>
                            <CustomTooltip
                              title={(stats?.unallocated_story_points_computed_by_cadence || 0) > 0
                                ? `${stats?.unallocated_story_points_computed_by_cadence} Story Points computed by Cadence Pro`
                                : 'All Story Points from Azure DevOps'}
                              placement="top"
                              arrow
                            >
                              <span className={`text-[10px] cursor-help ${(stats?.unallocated_story_points_computed_by_cadence || 0) > 0 ? 'text-yellow-500' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                            </CustomTooltip>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Unallocated</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-slate-100 dark:bg-slate-700/50"></div>

                    {/* Original Estimate Section */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Original Estimate</span>
                        <CustomTooltip
                          title="Maps to Original Estimate field in Azure DevOps for Task work items. 'Completed' shows the actual effort recorded when the task was finished."
                          placement="top"
                          arrow
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-slate-400 dark:text-slate-500 cursor-help hover:text-slate-600 dark:hover:text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 16v-4"></path>
                            <path d="M12 8h.01"></path>
                          </svg>
                        </CustomTooltip>
                      </div>

                      <div className="flex flex-col gap-0.5">
                        {/* Total • Completed */}
                        <div className="flex items-baseline flex-wrap gap-x-2 text-slate-300 dark:text-slate-600">
                          {/* Total */}
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold text-teal-600 dark:text-teal-400">
                              {(stats?.total_original_estimate || 0).toFixed(1)}<span className="text-sm font-medium ml-0.5">h</span>
                            </span>
                            <CustomTooltip
                              title={(stats?.original_estimate_computed_by_cadence || 0) > 0
                                ? `${(stats?.original_estimate_computed_by_cadence || 0).toFixed(1)} hrs Original Estimate computed by Cadence Pro`
                                : 'All Original Estimate from Azure DevOps'}
                              placement="top"
                              arrow
                            >
                              <span className={`text-[10px] cursor-help ${(stats?.original_estimate_computed_by_cadence || 0) > 0 ? 'text-yellow-500' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                            </CustomTooltip>
                          </div>

                          <span className="text-slate-300 dark:text-slate-600">|</span>

                          {/* Completed */}
                          <div className="flex items-center gap-0.5">
                            <span className={`text-sm font-bold ${(stats?.total_completed_original_estimate || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-600'}`}>
                              {(stats?.total_completed_original_estimate || 0).toFixed(1)}h
                            </span>
                            <CustomTooltip
                              title={(stats?.completed_original_estimate_computed_by_cadence || 0) > 0
                                ? `${(stats?.completed_original_estimate_computed_by_cadence || 0).toFixed(1)} hrs Completed Estimate computed by Cadence Pro`
                                : 'All Completed Estimate from Azure DevOps'}
                              placement="top"
                              arrow
                            >
                              <span className={`text-[10px] cursor-help ${(stats?.completed_original_estimate_computed_by_cadence || 0) > 0 ? 'text-yellow-500' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                            </CustomTooltip>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Completed</span>
                          </div>
                        </div>

                        {/* Unallocated (New Line) */}
                        {(stats?.total_unallocated_original_estimate ?? 0) > 0 && (
                          <div className="flex items-baseline gap-0.5 ml-0.5">
                            <span className="text-orange-500 dark:text-orange-400 text-xs font-medium mr-0.5">+</span>
                            <span className="text-sm font-bold text-orange-500 dark:text-orange-400">
                              {(stats?.total_unallocated_original_estimate || 0).toFixed(1)}h
                            </span>
                            <CustomTooltip
                              title={(stats?.unallocated_original_estimate_computed_by_cadence || 0) > 0
                                ? `${(stats?.unallocated_original_estimate_computed_by_cadence || 0).toFixed(1)} hrs Original Estimate computed by Cadence Pro`
                                : 'All Original Estimate from Azure DevOps'}
                              placement="top"
                              arrow
                            >
                              <span className={`text-[10px] cursor-help ${(stats?.unallocated_original_estimate_computed_by_cadence || 0) > 0 ? 'text-yellow-500' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                            </CustomTooltip>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Unallocated</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Pie Chart */}
                {isSectionVisible('status-overview') && (stats?.overall_status_breakdown && Object.keys(stats.overall_status_breakdown).filter(k => k !== 'total').length > 0 ? (
                  <div style={{ order: sectionOrderMap['status-overview'] ?? 1 }} className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                    <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
                      Status Overview
                    </h4>
                    <div className="flex justify-center mb-3">
                      <DonutChart
                        data={Object.entries(stats.overall_status_breakdown)
                          .filter(([key]) => key !== 'total')
                          .map(([status, count]) => {
                            return {
                              label: status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                              value: count as number,
                              color: dashboardStatusHexColors[status] || '#9ca3af',
                              originalStatus: status
                            };
                          })}
                        onClick={(item: any) => {
                          if (onFilterRequest && item.originalStatus) {
                            // Pass the original status with underscores (e.g., 'on_track')
                            onFilterRequest('status', item.originalStatus);
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      {Object.entries(stats.overall_status_breakdown)
                        .filter(([key]) => key !== 'total')
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .map(([status, count], idx) => {
                          const totalTasks = stats.overall_status_breakdown.total || analytics.total;
                          return (
                            <StatusBar
                              key={idx}
                              label={status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              count={count as number}
                              total={totalTasks}
                              color={dashboardStatusHexColors[status] || '#9ca3af'}
                              points={0}
                              onClick={() => {
                                if (onFilterRequest) {
                                  // Pass the status with underscores (e.g., 'on_track')
                                  onFilterRequest('status', status);
                                }
                              }}
                            />
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div style={{ order: sectionOrderMap['status-overview'] ?? 1 }} className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                    <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
                      Status Overview
                    </h4>
                    <div className="flex justify-center mb-3">
                      <DonutChart
                        data={chartData}
                        onClick={(item: any) => {
                          if (onFilterRequest && item.originalStatus) {
                            // Pass the original status with underscores (e.g., 'on_track')
                            onFilterRequest('status', item.originalStatus);
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      {Object.values(analytics.byStatus)
                        .sort((a, b) => b.count - a.count)
                        .map((status: any, idx) => {
                          const originalStatus = Object.keys(analytics.byStatus).find(key => analytics.byStatus[key] === status) || '';
                          return (
                            <StatusBar
                              key={idx}
                              label={status.label}
                              count={status.count}
                              total={analytics.total}
                              color={status.color}
                              points={status.points}
                              onClick={() => {
                                if (onFilterRequest && originalStatus) {
                                  // Pass the status with underscores (e.g., 'on_track')
                                  onFilterRequest('status', originalStatus);
                                }
                              }}
                            />
                          );
                        })}
                    </div>
                  </div>
                ))}

                {/* Per Person Distribution */}
                {isSectionVisible('workload-by-person') && Object.keys(analytics.byPerson).length > 0 && (
                  <div style={{ order: sectionOrderMap['workload-by-person'] ?? 2 }} className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                    <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-gradient-to-b from-green-500 to-teal-500 rounded-full"></div>
                      Workload by Person
                    </h4>
                    <div className="space-y-1.5">
                      {Object.entries(analytics.byPerson)
                        .sort(([, a], [, b]) => b.count - a.count)
                        .slice(0, 10)
                        .map(([personId, person], idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              if (onFilterRequest) {
                                onFilterRequest('person', personId);
                              }
                            }}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <PersonBar
                              name={person.name}
                              total={person.count}
                              max={maxPersonTasks}
                              color={`hsl(${160 + idx * 20}, 70%, 50%)`}
                              points={person.points}
                              completedPoints={person.completedPoints}
                              originalEstimate={person.originalEstimate}
                              completedOriginalEstimate={person.completedOriginalEstimate}
                              computedPoints={person.computedPoints}
                              completedComputedPoints={person.completedComputedPoints}
                              computedOriginalEstimate={person.computedOriginalEstimate}
                              completedComputedOriginalEstimate={person.completedComputedOriginalEstimate}
                              byStatus={person.byStatus}
                              byType={person.byType}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Top Work Units */}
                {isSectionVisible('top-work-units') && Object.keys(analytics.byProject).length > 0 && (
                  <div style={{ order: sectionOrderMap['top-work-units'] ?? 3 }} className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                    <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
                      Top Work Units
                    </h4>
                    <div className="space-y-2">
                      {Object.values(analytics.byProject)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((project, idx) => (
                          <div key={idx} className="flex justify-between items-center text-[10px] border-b border-gray-100 dark:border-slate-700 pb-1.5">
                            <span className="text-light-text-secondary dark:text-gray-400 truncate mr-2" title={project.name}>
                              {project.name}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Combined SP & OE */}
                              <div className="flex items-center gap-1 text-[9px]">
                                <span className={`font-medium ${project.points > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                  {project.points.toFixed(1)} SP
                                </span>
                                <span className="text-gray-300 dark:text-gray-600">|</span>
                                <span className={`font-medium ${project.originalEstimate > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                  {project.originalEstimate.toFixed(1)} OE
                                </span>
                              </div>
                              <span className="font-bold text-light-text-primary dark:text-white">{project.count}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Task Type Breakdown - Pie Chart */}
                {isSectionVisible('work-item-types') && stats?.task_breakdown && Object.keys(stats.task_breakdown).length > 0 && (
                  <div style={{ order: sectionOrderMap['work-item-types'] ?? 4 }} className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                    <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full"></div>
                      Work Item Type Breakdown
                    </h4>
                    <div className="flex justify-center mb-3">
                      <DonutChart
                        data={Object.entries(stats.task_breakdown).map(([type, count], idx) => {
                          const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
                          return {
                            label: type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                            value: count as number,
                            color: colors[idx % colors.length],
                            originalType: type
                          };
                        })}
                        onClick={(item: any) => {
                          if (onFilterRequest && item.originalType) {
                            onFilterRequest('type', item.originalType);
                          }
                        }}
                      />
                    </div>

                    {/* Legend */}
                    <div className="space-y-2">
                      {Object.entries(stats.task_breakdown).map(([type, count], idx) => {
                        const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
                        const color = colors[idx % colors.length];
                        const label = type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded px-1 py-0.5 transition-colors"
                            onClick={() => {
                              if (onFilterRequest) {
                                onFilterRequest('type', type);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                              />
                              <span className="text-gray-700 dark:text-gray-300 font-medium">{label}</span>
                            </div>
                            <span className="font-bold text-gray-900 dark:text-white">{count as number}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ===== MULTI-SPRINT ANALYTICS (only when single team with multiple sprints selected) ===== */}
                {isSectionVisible('sprint-comparison') && shouldShowSprintCount && statsPerTeam && Object.keys(statsPerTeam).length > 1 && (() => {
                  // Transform stats_per_team into the format expected by the UI
                  // Keys are like "Atlas:Platform\\2025\\Q4\\Sprint 20251204"
                  const aggregatedSprintData: Record<string, {
                    storyPoints: { total: number; computed: number };
                    originalEstimate: { total: number; computed: number };
                    statusBreakdown: Record<string, number>;
                    taskBreakdown: Record<string, number>;
                    unallocatedStoryPoints: number;
                  }> = {};

                  Object.entries(statsPerTeam).forEach(([sprintKey, sprintStats]: [string, any]) => {
                    // Build status breakdown from overall_status_breakdown (excluding 'total')
                    const statusBreakdown: Record<string, number> = {};
                    if (sprintStats.overall_status_breakdown) {
                      Object.entries(sprintStats.overall_status_breakdown).forEach(([status, count]: [string, any]) => {
                        if (status !== 'total') {
                          statusBreakdown[status] = count as number;
                        }
                      });
                    }

                    aggregatedSprintData[sprintKey] = {
                      storyPoints: {
                        total: sprintStats.total_story_points || 0,
                        computed: sprintStats.story_points_computed_by_cadence || 0
                      },
                      originalEstimate: {
                        total: sprintStats.total_original_estimate || 0,
                        computed: sprintStats.original_estimate_computed_by_cadence || 0
                      },
                      statusBreakdown,
                      taskBreakdown: sprintStats.task_breakdown || {},
                      unallocatedStoryPoints: sprintStats.total_unallocated_story_points || 0
                    };
                  });

                  const sprintEntries = Object.entries(aggregatedSprintData);
                  if (sprintEntries.length === 0) return null;

                  // Helper to get sprint name from the full key
                  // Format: "Atlas:Platform\\2025\\Q4\\Sprint 20251204" -> "Sprint 20251204"
                  const getSprintName = (fullName: string) => {
                    const parts = fullName.split('\\');
                    // Get the last part which is the sprint name
                    return parts[parts.length - 1] || fullName;
                  };

                  // Helper to get sprint info with name and status
                  const getSprintInfo = (fullName: string) => {
                    const name = getSprintName(fullName);
                    const status = sprintStatusMap[name] || '';
                    return { name, status };
                  };

                  // Get sorted entries by date (extracted from sprint key - later sprints come last)
                  const sortedByDate = [...sprintEntries].sort(([a], [b]) => {
                    const sprintNameA = getSprintName(a);
                    const sprintNameB = getSprintName(b);
                    return sprintNameA.localeCompare(sprintNameB);
                  });

                  return (
                    <div style={{ order: sectionOrderMap['sprint-comparison'] ?? 5 }} className="flex flex-col gap-4">
                      {/* Sprint Comparison - Clean List View */}
                      <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                        <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                          <div className="w-1 h-4 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full"></div>
                          Sprint Comparison
                        </h4>

                        {/* Sprint List */}
                        <div className="space-y-2">
                          {sortedByDate.map(([sprintKey, data], idx) => {
                            const sprintInfo = getSprintInfo(sprintKey);
                            const totalTasks = Object.values(data.statusBreakdown).reduce((sum, count) => sum + count, 0);

                            return (
                              <div
                                key={sprintKey}
                                className="py-3 border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                              >
                                {/* Row 1: Sprint name with badge + metrics inline */}
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                    <span className="text-sm font-semibold text-gray-800 dark:text-white truncate" title={sprintKey}>
                                      {sprintInfo.name}
                                    </span>
                                    {sprintInfo.status && (
                                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full border flex-shrink-0 ${getSprintStatusBadgeClasses(sprintInfo.status)}`}>
                                        {sprintInfo.status}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs flex-shrink-0">
                                    <div className="flex items-center gap-1">
                                      <span className="text-gray-500 dark:text-gray-400">Items</span>
                                      <span className="font-bold text-gray-800 dark:text-white">{totalTasks}</span>
                                    </div>
                                    {/* Combined SP & OE */}
                                    <div className="flex items-center gap-1">
                                      <span className={`font-medium ${data.storyPoints.total > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {data.storyPoints.total.toFixed(0)} SP
                                      </span>
                                      <span className="text-gray-300 dark:text-gray-600">|</span>
                                      <span className={`font-medium ${data.originalEstimate.total > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {data.originalEstimate.total.toFixed(1)} OE
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Row 2: Status bar with counts inside */}
                                {Object.keys(data.statusBreakdown).length > 0 && (
                                  <div className="flex gap-1 h-5">
                                    {Object.entries(data.statusBreakdown)
                                      .filter(([, count]) => (count as number) > 0)
                                      .sort(([, a], [, b]) => (b as number) - (a as number))
                                      .map(([status, count], segIdx, arr) => {
                                        const widthPercent = totalTasks > 0 ? ((count as number) / totalTasks) * 100 : 0;
                                        return (
                                          <div
                                            key={status}
                                            className={`h-full flex items-center justify-center text-[10px] font-bold text-white min-w-[24px] cursor-pointer relative group ${segIdx === 0 ? 'rounded-l-md' : ''} ${segIdx === arr.length - 1 ? 'rounded-r-md' : ''}`}
                                            style={{
                                              flex: widthPercent,
                                              backgroundColor: dashboardStatusHexColors[status] || '#9ca3af'
                                            }}
                                          >
                                            {count}
                                            {/* Fast tooltip */}
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[9px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none z-10">
                                              {status.replace(/_/g, ' ')}
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Work Item Types by Sprint */}
                      <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                        <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                          <div className="w-1 h-4 bg-gradient-to-b from-teal-500 to-emerald-500 rounded-full"></div>
                          Work Item Types by Sprint
                        </h4>
                        <div className="space-y-3">
                          {sprintEntries
                            .filter(([, data]) => Object.keys(data.taskBreakdown).length > 0)
                            .map(([sprintKey, data], idx) => {
                              const sprintInfo = getSprintInfo(sprintKey);
                              return (
                                <div key={sprintKey} className="space-y-1.5">
                                  <div className="flex items-center gap-1.5" title={sprintKey}>
                                    <span className="text-[10px] text-gray-700 dark:text-gray-300 font-medium truncate">
                                      {sprintInfo.name}
                                    </span>
                                    {sprintInfo.status && (
                                      <span className={`px-1.5 py-0.5 text-[8px] font-semibold rounded-full border flex-shrink-0 ${getSprintStatusBadgeClasses(sprintInfo.status)}`}>
                                        {sprintInfo.status}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(data.taskBreakdown)
                                      .sort(([, a], [, b]) => (b as number) - (a as number))
                                      .map(([taskType, count]) => (
                                        <div
                                          key={taskType}
                                          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-medium capitalize ${getItemTypeColor(taskType)}`}
                                        >
                                          {taskType.replace(/_/g, ' ')}
                                          <span className="font-bold">{count}</span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ===== MULTI-TEAM ANALYTICS (only when multiple teams selected) ===== */}
                {isSectionVisible('team-comparison') && selectedTeams && selectedTeams.length > 1 && statsPerTeam && Object.keys(statsPerTeam).length > 0 && (() => {
                  // Use pre-aggregated stats_per_team from API response

                  // Transform stats_per_team into the format expected by the UI
                  const aggregatedTeamData: Record<string, {
                    storyPoints: { total: number; computed: number };
                    originalEstimate: { total: number; computed: number };
                    statusBreakdown: Record<string, number>;
                    taskBreakdown: Record<string, number>;
                    unallocatedStoryPoints: number;
                  }> = {};

                  Object.entries(statsPerTeam).forEach(([teamKey, teamStats]: [string, any]) => {
                    // Build status breakdown from overall_status_breakdown (excluding 'total')
                    const statusBreakdown: Record<string, number> = {};
                    if (teamStats.overall_status_breakdown) {
                      Object.entries(teamStats.overall_status_breakdown).forEach(([status, count]: [string, any]) => {
                        if (status !== 'total') {
                          statusBreakdown[status] = count as number;
                        }
                      });
                    }

                    aggregatedTeamData[teamKey] = {
                      storyPoints: {
                        total: teamStats.total_story_points || 0,
                        computed: teamStats.story_points_computed_by_cadence || 0
                      },
                      originalEstimate: {
                        total: teamStats.total_original_estimate || 0,
                        computed: teamStats.original_estimate_computed_by_cadence || 0
                      },
                      statusBreakdown,
                      taskBreakdown: teamStats.task_breakdown || {},
                      unallocatedStoryPoints: teamStats.total_unallocated_story_points || 0
                    };
                  });

                  const teamEntries = Object.entries(aggregatedTeamData);
                  if (teamEntries.length === 0) return null;

                  // Helper to get team name in format "Team (Project)"
                  const getShortTeamName = (fullName: string) => {
                    // Format: "Atlas:WAO Trading\2025\Q4\Sprint 20251204" -> "Atlas (WAO Trading)"
                    const parts = fullName.split(':');
                    if (parts.length >= 2) {
                      const teamName = parts[0]; // e.g., "Atlas"
                      const projectPart = parts[1].split('\\')[0]; // e.g., "WAO Trading"
                      return projectPart ? `${teamName} (${projectPart})` : teamName;
                    }
                    return fullName.split('\\')[0];
                  };

                  const maxStoryPoints = Math.max(...teamEntries.map(([, data]) => data.storyPoints.total), 1);
                  const maxTasks = Math.max(...teamEntries.map(([, data]) =>
                    Object.values(data.statusBreakdown).reduce((sum, count) => sum + count, 0)
                  ), 1);

                  // Get sorted entries for comparison
                  const sortedByPoints = [...teamEntries].sort(([, a], [, b]) => b.storyPoints.total - a.storyPoints.total);

                  return (
                    <div style={{ order: sectionOrderMap['team-comparison'] ?? 6 }} className="flex flex-col gap-4">
                      {/* Team Comparison - Clean List View */}
                      <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                        <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                          <div className="w-1 h-4 bg-gradient-to-b from-violet-500 to-fuchsia-500 rounded-full"></div>
                          Team Comparison
                        </h4>

                        {/* Team List */}
                        <div className="space-y-2">
                          {sortedByPoints.map(([teamKey, data], idx) => {
                            const shortName = getShortTeamName(teamKey);
                            const totalTasks = Object.values(data.statusBreakdown).reduce((sum, count) => sum + count, 0);

                            return (
                              <div
                                key={teamKey}
                                className="py-3 border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                              >
                                {/* Row 1: Team name + metrics inline */}
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold text-gray-800 dark:text-white truncate max-w-[140px]" title={teamKey}>
                                    {shortName}
                                  </span>
                                  <div className="flex items-center gap-3 text-xs flex-shrink-0">
                                    <div className="flex items-center gap-1">
                                      <span className="text-gray-500 dark:text-gray-400">Items</span>
                                      <span className="font-bold text-gray-800 dark:text-white">{totalTasks}</span>
                                    </div>
                                    {/* Combined SP & OE */}
                                    <div className="flex items-center gap-1">
                                      <span className={`font-medium ${data.storyPoints.total > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {data.storyPoints.total.toFixed(0)} SP
                                      </span>
                                      <span className="text-gray-300 dark:text-gray-600">|</span>
                                      <span className={`font-medium ${data.originalEstimate.total > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {data.originalEstimate.total.toFixed(1)} OE
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Row 2: Status bar with counts inside */}
                                {Object.keys(data.statusBreakdown).length > 0 && (
                                  <div className="flex gap-1 h-5">
                                    {Object.entries(data.statusBreakdown)
                                      .filter(([, count]) => (count as number) > 0)
                                      .sort(([, a], [, b]) => (b as number) - (a as number))
                                      .map(([status, count], segIdx, arr) => {
                                        const widthPercent = totalTasks > 0 ? ((count as number) / totalTasks) * 100 : 0;
                                        return (
                                          <div
                                            key={status}
                                            className={`h-full flex items-center justify-center text-[10px] font-bold text-white min-w-[24px] cursor-pointer relative group ${segIdx === 0 ? 'rounded-l-md' : ''} ${segIdx === arr.length - 1 ? 'rounded-r-md' : ''}`}
                                            style={{
                                              flex: widthPercent,
                                              backgroundColor: dashboardStatusHexColors[status] || '#9ca3af'
                                            }}
                                          >
                                            {count}
                                            {/* Fast tooltip */}
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[9px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none z-10">
                                              {status.replace(/_/g, ' ')}
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* ===== WORK ITEM TYPES BY TEAM (only when multiple teams selected) ===== */}
                      <div className="bg-white dark:bg-slate-800/30 p-4 rounded-xl border border-light-border-default dark:border-slate-700">
                        <h4 className="text-[10px] font-bold text-light-text-primary dark:text-white uppercase tracking-wide mb-3 flex items-center gap-2">
                          <div className="w-1 h-4 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full"></div>
                          Work Item Types by Team
                        </h4>
                        <div className="space-y-3">
                          {teamEntries
                            .filter(([, data]) => Object.keys(data.taskBreakdown).length > 0)
                            .map(([teamKey, data], idx) => {
                              const shortName = getShortTeamName(teamKey);
                              return (
                                <div key={teamKey} className="space-y-1.5">
                                  <div className="text-[10px] text-gray-700 dark:text-gray-300 font-medium truncate" title={teamKey}>
                                    {shortName}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(data.taskBreakdown)
                                      .sort(([, a], [, b]) => (b as number) - (a as number))
                                      .map(([taskType, count]) => (
                                        <div
                                          key={taskType}
                                          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-medium capitalize ${getItemTypeColor(taskType)}`}
                                        >
                                          {taskType.replace(/_/g, ' ')}
                                          <span className="font-bold">{count}</span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>

                  );
                })()}
              </div>
            )
            }
          </div >

          {/* Scroll Down Indicator */}
          {
            showScrollIndicator && !isLoading && (
              <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
                {/* Gradient fade effect */}
                <div className="h-16 bg-gradient-to-t from-white dark:from-slate-900 to-transparent" />
                {/* Clickable button */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-auto">
                  <button
                    onClick={handleScrollDown}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-medium rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 animate-bounce"
                    style={{ animationDuration: '2s' }}
                    title="More content below"
                  >
                    <span>More</span>
                    <HiChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          }

          {/* Section Customizer Overlay */}
          <SectionCustomizer
            isOpen={showCustomizer}
            onClose={() => setShowCustomizer(false)}
            sections={sectionPreferences}
            onSectionsChange={setSectionPreferences}
            showSprintComparison={shouldShowSprintCount && statsPerTeam && Object.keys(statsPerTeam).length > 1}
            showTeamComparison={selectedTeams && selectedTeams.length > 1 && statsPerTeam && Object.keys(statsPerTeam).length > 0}
          />
        </div >
      </div >

      {/* Tooltip Portal - Rendered outside to avoid overflow issues */}
      {
        showTeamTooltip && (
          <Portal>
            <div
              className="fixed z-[10000] px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg pointer-events-none min-w-[140px] max-w-[200px]"
              style={{
                top: tooltipPos.top,
                left: tooltipPos.left - 8, // Offset slightly to the left
                transform: 'translate(-100%, -50%)' // Center vertically and move to left of cursor
              }}
            >
              <div className="text-[10px] font-semibold text-gray-300 uppercase mb-1">Selected Teams</div>
              <div className="space-y-0.5">
                {selectedTeamNames.map((name, idx) => (
                  <div key={idx} className="text-white font-medium truncate">• {name}</div>
                ))}
              </div>
              {/* Arrow pointing right */}
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 -ml-1 border-4 border-transparent border-l-gray-900 dark:border-l-gray-700"></div>
            </div>
          </Portal>
        )
      }

      {/* Sprint Tooltip Portal - Rendered outside to avoid overflow issues */}
      {
        showSprintTooltip && (
          <Portal>
            <div
              className="fixed z-[10000] px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg pointer-events-none min-w-[140px] max-w-[220px]"
              style={{
                top: sprintTooltipPos.top,
                left: sprintTooltipPos.left - 8,
                transform: 'translate(-100%, -50%)'
              }}
            >
              <div className="text-[10px] font-semibold text-gray-300 uppercase mb-2">Sprints in View</div>
              <div className="space-y-1.5">
                {sprintInfoForDisplay.map((sprint: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-white font-medium truncate">• {sprint.name}</span>
                    <span className={`px-1.5 py-0.5 text-[8px] font-semibold rounded-full border flex-shrink-0 ${sprint.badgeClasses}`}>
                      {sprint.status}
                    </span>
                  </div>
                ))}
              </div>
              {/* Arrow pointing right */}
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 -ml-1 border-4 border-transparent border-l-gray-900 dark:border-l-gray-700"></div>
            </div>
          </Portal>
        )
      }
    </Portal >
  );
};

export default TaskAnalyticsSidebar;
